import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getConfig, type PAConfig } from '../api/payments';

/**
 * ============================================================================
 * THE OPERATOR'S OWN WORDS — `GET /pa/config`, the listener's one PUBLIC read
 * ============================================================================
 *
 * # What it closes
 *
 * W1b could not build eight controls of `ui-enduser-personal-area` §1 and §"Global
 * chrome" — the product title, the login welcome text, the terms-of-use link, the support
 * telephone and email, the company block, the copyright line — for one reason, recorded
 * at the time: they are all operator-configured and **this listener exposed no public
 * configuration read**. It does now (D258), and this provider is what puts the values on
 * the screen.
 *
 * # ⚠ IT IS FETCHED OUTSIDE THE SESSION, AND IT HAS TO BE
 *
 * The LOGIN SCREEN renders the welcome text and the footer, and nobody is logged in
 * there. So this provider sits ABOVE `SessionProvider` in `App.tsx` and its request
 * carries no session; the route is unauthenticated by design, publishing an explicit
 * allow-list of seven strings rather than a `personal_area.` prefix dump — the same
 * namespace also holds the brute-force bounds, and publishing those would tell an
 * unauthenticated caller exactly how many guesses he gets.
 *
 * # ⚠ IT NEVER BLOCKS AND NEVER FAILS
 *
 * There is no loading state and no error state, and that is the whole design: every field
 * is a string that DEFAULTS EMPTY, an empty field renders NOTHING, and a failed read is
 * indistinguishable from an unconfigured installation. A portal whose login form waited
 * for its footer would be a portal a subscriber cannot enter when a settings read is
 * slow, and «the operator has not written a support telephone» and «we could not read the
 * settings» are the same fact to him: there is no number to show either way.
 *
 * That is also why nothing here is invented. A fallback telephone number or a fallback
 * company name would be somebody else's operator printed on this one's cabinet, which is
 * worse than an absent line — the same argument the seeder makes for its placeholder
 * instruction blocks.
 */

const EMPTY: PAConfig = {
  product_title: '',
  login_welcome_text: '',
  support_phone: '',
  support_email: '',
  company_info: '',
  copyright_text: '',
  terms_url: '',
};

const PortalConfigContext = createContext<PAConfig>(EMPTY);

/** usePortalConfig is the operator's text, all-empty until (and unless) the read lands. */
export function usePortalConfig(): PAConfig {
  return useContext(PortalConfigContext);
}

export function PortalConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PAConfig>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await getConfig();
        if (!cancelled) setConfig(loaded);
      } catch {
        // Deliberately silent — see the header. There is no screen state for «the
        // operator's footer could not be read», because the honest rendering of that
        // state is the same as the honest rendering of «he has not written one».
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The object identity changes only when the read lands, so the memo is what keeps the
  // whole authenticated tree from re-rendering on every parent render.
  const value = useMemo(() => config, [config]);
  return <PortalConfigContext.Provider value={value}>{children}</PortalConfigContext.Provider>;
}
